import React, { useMemo } from 'react';
import ReportHeader from './ReportHeader';
import ReportFilterBar from './ReportFilterBar';
import ReportTable from './ReportTable';
import ReportModal from './ReportModal';
import ReportAttachments from './ReportAttachments';

/**
 * Reusable Report Table and Modal layout component.
 * Composed from modular components: ReportHeader, ReportFilterBar, ReportTable, ReportModal, ReportAttachments.
 */
const ReportLayout = ({
  // Header / Actions
  title = 'Reports',
  addButtonLabel = 'Add New Report',
  onAddNew,
  showAddButton = true,

  // Search & Filter
  searchTerm = '',
  onSearchChange,
  searchPlaceholder = 'Search reports by date or author...',
  statusFilter = 0,
  onStatusFilterChange,
  statusOptions = [
    { value: 0, label: 'Active' },
    { value: 1, label: 'Archived' },
  ],

  // Table
  columns = [
    { key: 'date', label: 'Date' },
    { key: 'submitted', label: 'Submitted By' },
    { key: 'last_modified', label: 'Last Modified' },
  ],
  data = [],
  isLoading = false,
  emptyMessage,
  emptySubMessage,
  onView,
  onEdit,
  onArchive,
  onRestore,

  // Modal Configuration
  isModalOpen = false,
  onCloseModal,
  modalTitle,
  modalSubtitle,
  modalMode = 'Add', // 'Add' | 'Edit' | 'View'
  modalSize = 'xl',

  // Modal Body Content (custom per report type)
  renderModalContent,

  // Attachments Section
  enableAttachments = false,
  attachments = [],
  onUploadFiles,
  onRemoveAttachment,
  acceptedFileTypes = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.mp4,.webm,.avi,.mov',
  attachmentFileInputId = 'report-file-input',

  // Modal Footer
  isFormValid = true,
  isSubmitting = false,
  onSubmit,
  onUpdate,
  submitButtonLabel = 'Submit',
  updateButtonLabel = 'Update',
  closeButtonLabel = 'Close',
  cancelButtonLabel = 'Cancel',
}) => {
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const term = searchTerm.toLowerCase().trim();
    return data.filter((item) =>
      columns.some((col) => {
        const val = item[col.key];
        return val && String(val).toLowerCase().includes(term);
      })
    );
  }, [data, searchTerm, columns]);

  const defaultEmptySub = useMemo(() => {
    if (searchTerm) return 'Try clearing your search query';
    if (statusFilter === 0) return `Create your first ${title.toLowerCase()} to get started`;
    return 'No archived reports available';
  }, [searchTerm, statusFilter, title]);

  const resolvedModalTitle =
    modalTitle ||
    (modalMode === 'Add'
      ? `New ${title}`
      : modalMode === 'Edit'
        ? `Edit ${title}`
        : `${title} Details`);

  const resolvedModalSubtitle =
    modalSubtitle ||
    (modalMode === 'View'
      ? 'Review recorded information and attachments.'
      : 'Fill in the details and optional attachments.');

  return (
    <div className="w-full pb-8 font-sans">
      {/* Header and Controls */}
      <ReportHeader
        showAddButton={showAddButton}
        statusFilter={statusFilter}
        onAddNew={onAddNew}
        addButtonLabel={addButtonLabel}
      />

      {/* Filter and Search Bar */}
      <ReportFilterBar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        statusOptions={statusOptions}
      />

      {/* Table Container */}
      <ReportTable
        columns={columns}
        data={filteredData}
        isLoading={isLoading}
        statusFilter={statusFilter}
        title={title}
        emptyMessage={emptyMessage}
        emptySubMessage={emptySubMessage || defaultEmptySub}
        onView={onView}
        onEdit={onEdit}
        onArchive={onArchive}
        onRestore={onRestore}
      />

      {/* Modernized Modal */}
      <ReportModal
        isOpen={isModalOpen}
        onClose={onCloseModal}
        title={resolvedModalTitle}
        subtitle={resolvedModalSubtitle}
        size={modalSize}
        modalMode={modalMode}
        isFormValid={isFormValid}
        isSubmitting={isSubmitting}
        onSubmit={onSubmit}
        onUpdate={onUpdate}
        submitButtonLabel={submitButtonLabel}
        updateButtonLabel={updateButtonLabel}
        closeButtonLabel={closeButtonLabel}
        cancelButtonLabel={cancelButtonLabel}
      >
        {renderModalContent && renderModalContent({ modalMode, isModalOpen })}

        {enableAttachments && (
          <ReportAttachments
            attachments={attachments}
            modalMode={modalMode}
            onUploadFiles={onUploadFiles}
            onRemoveAttachment={onRemoveAttachment}
            acceptedFileTypes={acceptedFileTypes}
            fileInputId={attachmentFileInputId}
          />
        )}
      </ReportModal>
    </div>
  );
};

export default ReportLayout;
